// src/constants/portfolioData.ts
import {
  // Import all assets and utilities
  normalizeCategory,
  getAssetOrPlaceholder,
  MediaAsset,

  // Import residential service assets
  pantryBefore1,
  pantryAfter1,
  closetBefore1,
  closetAfter1,
  closet1BeforeGallery,
  closet1AfterGallery,
  kitchenBefore1,
  kitchenAfter1,
  officeBefore1,
  officeAfter1,
  playRoomBefore1,
  playRoomAfter1,
  laundryBefore1,
  laundryAfter1,
  movingBefore1,
  movingAfter1,
  stylingBefore1,
  stylingAfter1,
  maintenanceBefore1,
  maintenanceAfter1,

  // Commercial service assets
  officeSpaceBefore1,
  officeSpaceAfter1,
  supplyRoomBefore1,
  supplyRoomAfter1,
  breakRoomBefore1,
  breakRoomAfter1,
  receptionBefore1,
  receptionAfter1,

  // Add-on service assets
  virtualBefore1,
  virtualAfter1,
  giftCertificateBefore1,
  giftCertificateAfter1,
  takedownBefore1, // ADD THIS IMPORT
  takedownAfter1, // ADD THIS IMPORT
} from './portfolioAssets'

// Define media source type
type MediaSource = MediaAsset

export interface PortfolioItem {
  id: string
  title: string
  category: string
  description: string
  details?: string
  clientName?: string
  beforeMedia: MediaSource
  afterMedia: MediaSource
  beforeMediaType?: 'image' | 'video' | 'gallery'
  afterMediaType?: 'image' | 'video' | 'gallery'
  challenge?: string
  solution?: string
  featured?: boolean
  serviceType: 'residential' | 'commercial' | 'addon'
}

// UPDATED SERVICE CATEGORIES - ONLY YOUR SPECIFIED SERVICES
export const serviceCategories = [
  // Residential Services
  'Pantry Organization',
  'Kitchen Organization',
  'Closet Makeovers',
  'Home Office Setup',
  'Playroom Organization',
  'Laundry Room Setup',
  'New Home Transition Organizing',
  'Home Styling',
  'Maintenance Plans',

  // Commercial Services
  'Office Space Organization',
  'Supply Room Setup',
  'Break Room Organization',
  'Reception & Lobby',

  // Add-On Services
  'Virtual Consultation',
  'Sorted & Organized Gift Certificates',
  'Seasonal Setup and Takedown',
]

// Updated service type mapping
export const serviceTypes: Record<string, 'residential' | 'commercial' | 'addon'> = {
  // Residential Services
  'Pantry Organization': 'residential',
  'Kitchen Organization': 'residential',
  'Closet Makeovers': 'residential',
  'Home Office Setup': 'residential',
  'Playroom Organization': 'residential',
  'Laundry Room Setup': 'residential',
  'New Home Transition Organizing': 'residential',
  'Home Styling': 'residential',
  'Maintenance Plans': 'residential',

  // Commercial Services
  'Office Space Organization': 'commercial',
  'Supply Room Setup': 'commercial',
  'Break Room Organization': 'commercial',
  'Reception & Lobby': 'commercial',

  // Add-On Services
  'Virtual Consultation': 'addon',
  'Sorted & Organized Gift Certificates': 'addon',
  'Seasonal Setup and Takedown': 'addon',
}

// PORTFOLIO ITEMS - UPDATED TO MATCH YOUR SERVICE LIST
export const portfolioItems: PortfolioItem[] = [
  // ============= RESIDENTIAL SERVICES =============
  {
    id: 'pantry-1',
    title: 'Modern Pantry Transformation',
    category: 'Pantry Organization',
    description:
      'Complete pantry overhaul with clear containers, labeled zones, and optimized storage.',
    clientName: 'Johnson Family',
    beforeMedia: getAssetOrPlaceholder(pantryBefore1, 'before', 'Pantry Organization'),
    afterMedia: getAssetOrPlaceholder(pantryAfter1, 'after', 'Pantry Organization'),
    challenge: 'Deep pantry with items getting lost, expired products, and no organization system.',
    solution:
      'Implemented clear stackable containers, turntables for corners, and zone system for food categories.',
    featured: true,
    serviceType: 'residential',
  },
  {
    id: 'kitchen-1',
    title: 'Kitchen Organization Makeover',
    category: 'Kitchen Organization',
    description:
      'Maximized kitchen storage with drawer dividers, cabinet organizers, and workflow optimization.',
    clientName: 'Wilson Family',
    beforeMedia: getAssetOrPlaceholder(kitchenBefore1, 'before', 'Kitchen Organization'),
    afterMedia: getAssetOrPlaceholder(kitchenAfter1, 'after', 'Kitchen Organization'),
    challenge: 'Overflowing cabinets with mismatched containers and inefficient space usage.',
    solution:
      'Implemented drawer dividers, stackable shelving, and created cooking zones for better workflow.',
    featured: true,
    serviceType: 'residential',
  },
  {
    id: 'closet-1',
    title: 'Master Closet Makeover',
    category: 'Closet Makeovers',
    description:
      'Transformed overflowing closet into boutique-style space with color coordination.',
    clientName: 'Emily R.',
    beforeMedia:
      closet1BeforeGallery.length > 0 && closet1BeforeGallery[0] !== ''
        ? closet1BeforeGallery
        : getAssetOrPlaceholder(closetBefore1, 'before', 'Closet Makeovers'),
    afterMedia:
      closet1AfterGallery.length > 0 && closet1AfterGallery[0] !== ''
        ? closet1AfterGallery
        : getAssetOrPlaceholder(closetAfter1, 'after', 'Closet Makeovers'),
    beforeMediaType:
      closet1BeforeGallery.length > 1 && closet1BeforeGallery[0] !== '' ? 'gallery' : 'image',
    afterMediaType:
      closet1AfterGallery.length > 1 && closet1AfterGallery[0] !== '' ? 'gallery' : 'image',
    challenge: 'Too many clothes in small space, difficult to find items, causing wrinkles.',
    solution:
      'Sorted by season and category, color-coding system, specialized hangers and organizers.',
    featured: true,
    serviceType: 'residential',
  },
  {
    id: 'office-1',
    title: 'Home Office Setup',
    category: 'Home Office Setup',
    description: 'Created productive workspace with streamlined storage and ergonomic layout.',
    clientName: 'James T.',
    beforeMedia: getAssetOrPlaceholder(officeBefore1, 'before', 'Home Office Setup'),
    afterMedia: getAssetOrPlaceholder(officeAfter1, 'after', 'Home Office Setup'),
    challenge: 'Scattered papers and supplies, difficult to focus and find documents.',
    solution: 'Created filing system, desktop organization, and optimized layout for productivity.',
    featured: true,
    serviceType: 'residential',
  },
  {
    id: 'playroom-1',
    title: 'Playroom Organization',
    category: 'Playroom Organization',
    description:
      'Converted chaotic playroom into organized space encouraging creativity and easy cleanup.',
    clientName: 'Henderson Family',
    beforeMedia: getAssetOrPlaceholder(playRoomBefore1, 'before', 'Playroom Organization'),
    afterMedia: getAssetOrPlaceholder(playRoomAfter1, 'after', 'Playroom Organization'),
    challenge: 'Toys everywhere with no storage system, making cleanup difficult.',
    solution: 'Zone system for activities, labeled bins, and toy rotation system.',
    featured: false,
    serviceType: 'residential',
  },
  {
    id: 'laundry-1',
    title: 'Laundry Room Setup',
    category: 'Laundry Room Setup',
    description: 'Transformed cluttered laundry area into functional space with clear workflow.',
    clientName: 'Thompson Family',
    beforeMedia: getAssetOrPlaceholder(laundryBefore1, 'before', 'Laundry Room Setup'),
    afterMedia: getAssetOrPlaceholder(laundryAfter1, 'after', 'Laundry Room Setup'),
    challenge: 'Small laundry room overcrowded with products, no clear process.',
    solution: 'Floating shelves, consistent containers, workflow system with sorting bins.',
    featured: false,
    serviceType: 'residential',
  },
  {
    id: 'moving-1',
    title: 'New Home Transition Organizing',
    category: 'New Home Transition Organizing',
    description:
      'Assisted family with move, organizing as they unpacked for functional systems from day one.',
    clientName: 'Martinez Family',
    beforeMedia: getAssetOrPlaceholder(movingBefore1, 'before', 'New Home Transition Organizing'),
    afterMedia: getAssetOrPlaceholder(movingAfter1, 'after', 'New Home Transition Organizing'),
    challenge: 'Moving to new home with different storage configurations.',
    solution: 'Strategic unpacking plan, established organizing systems for each space.',
    featured: true,
    serviceType: 'residential',
  },
  {
    id: 'styling-1',
    title: 'Home Styling',
    category: 'Home Styling',
    description: 'Added finishing touches to newly organized spaces for aesthetic appeal.',
    clientName: 'Chen Family',
    beforeMedia: getAssetOrPlaceholder(stylingBefore1, 'before', 'Home Styling'),
    afterMedia: getAssetOrPlaceholder(stylingAfter1, 'after', 'Home Styling'),
    challenge: 'Organized space lacked visual harmony and personal style.',
    solution: 'Selected decorative elements, created visual balance with personal touches.',
    featured: false,
    serviceType: 'residential',
  },
  {
    id: 'maintenance-1',
    title: 'Maintenance Plans',
    category: 'Maintenance Plans',
    description: 'Regular refresh sessions keeping organization systems functioning year-round.',
    clientName: 'Wright Family',
    beforeMedia: getAssetOrPlaceholder(maintenanceBefore1, 'before', 'Maintenance Plans'),
    afterMedia: getAssetOrPlaceholder(maintenanceAfter1, 'after', 'Maintenance Plans'),
    challenge: 'Maintaining systems over time with changing seasons and needs.',
    solution: 'Quarterly check-ins, system adjustments, seasonal rotations.',
    featured: false,
    serviceType: 'residential',
  },

  // ============= COMMERCIAL SERVICES =============
  {
    id: 'commercial-office-1',
    title: 'Office Space Organization',
    category: 'Office Space Organization',
    description: 'Reorganized office floor to improve workflow and productivity.',
    clientName: 'Meridian Technologies',
    beforeMedia: getAssetOrPlaceholder(officeSpaceBefore1, 'before', 'Office Space Organization'),
    afterMedia: getAssetOrPlaceholder(officeSpaceAfter1, 'after', 'Office Space Organization'),
    challenge: 'Inefficient space creating workflow bottlenecks.',
    solution: 'Redesigned layout, organized supply stations, document management system.',
    featured: true,
    serviceType: 'commercial',
  },
  {
    id: 'supply-room-1',
    title: 'Supply Room Setup',
    category: 'Supply Room Setup',
    description: 'Transformed chaotic supply closet into efficient inventory system.',
    clientName: 'Northstar Medical',
    beforeMedia: getAssetOrPlaceholder(supplyRoomBefore1, 'before', 'Supply Room Setup'),
    afterMedia: getAssetOrPlaceholder(supplyRoomAfter1, 'after', 'Supply Room Setup'),
    challenge: 'Difficult to locate supplies, challenging inventory tracking.',
    solution: 'Category-based system with clear labeling and reordering protocols.',
    featured: false,
    serviceType: 'commercial',
  },
  {
    id: 'break-room-1',
    title: 'Break Room Organization',
    category: 'Break Room Organization',
    description: 'Revitalized break room into refreshing space promoting relaxation.',
    clientName: 'Venture Partners LLC',
    beforeMedia: getAssetOrPlaceholder(breakRoomBefore1, 'before', 'Break Room Organization'),
    afterMedia: getAssetOrPlaceholder(breakRoomAfter1, 'after', 'Break Room Organization'),
    challenge: 'Disorganized break room with inefficient storage.',
    solution: 'Organized storage, labeled zones, improved layout for social interaction.',
    featured: false,
    serviceType: 'commercial',
  },
  {
    id: 'reception-1',
    title: 'Reception & Lobby',
    category: 'Reception & Lobby',
    description: 'Transformed cluttered reception into inviting, organized first impression.',
    clientName: 'Northstar Medical',
    beforeMedia: getAssetOrPlaceholder(receptionBefore1, 'before', 'Reception & Lobby'),
    afterMedia: getAssetOrPlaceholder(receptionAfter1, 'after', 'Reception & Lobby'),
    challenge: 'Unwelcoming reception with disorganized paperwork.',
    solution: 'Streamlined check-in workflow, organized materials, improved layout.',
    featured: false,
    serviceType: 'commercial',
  },

  // ============= ADD-ON SERVICES =============
  {
    id: 'virtual-1',
    title: 'Virtual Consultation / Consultoría en Español',
    category: 'Virtual Consultation',
    description: 'Remote organizing guidance available in English and Spanish.',
    clientName: 'Remote Clients',
    beforeMedia: getAssetOrPlaceholder(virtualBefore1, 'before', 'Virtual Consultation'),
    afterMedia: getAssetOrPlaceholder(virtualAfter1, 'after', 'Virtual Consultation'),
    challenge: 'Clients needing guidance without in-person visits.',
    solution: 'Virtual assessments, detailed plans, step-by-step implementation support.',
    featured: false,
    serviceType: 'addon',
  },
  {
    id: 'gift-certificate-1',
    title: 'Sorted & Organized Gift Certificates',
    category: 'Sorted & Organized Gift Certificates',
    description: 'Custom organization services as the perfect gift.',
    clientName: 'Gift Recipients',
    beforeMedia: getAssetOrPlaceholder(
      giftCertificateBefore1,
      'before',
      'Sorted & Organized Gift Certificates'
    ),
    afterMedia: getAssetOrPlaceholder(
      giftCertificateAfter1,
      'after',
      'Sorted & Organized Gift Certificates'
    ),
    challenge: 'Creating personalized gift experiences for organization needs.',
    solution: 'Beautiful custom certificates with consultation included.',
    featured: false,
    serviceType: 'addon',
  },
  {
    id: 'seasonal-1',
    title: 'Seasonal Setup and Takedown',
    category: 'Seasonal Setup and Takedown',
    description: 'Professional setup and storage of seasonal decorations and items.',
    clientName: 'Various Clients',
    // FIX: Use the actual takedown images instead of undefined
    beforeMedia: getAssetOrPlaceholder(takedownBefore1, 'before', 'Seasonal Setup and Takedown'),
    afterMedia: getAssetOrPlaceholder(takedownAfter1, 'after', 'Seasonal Setup and Takedown'),
    challenge: 'Managing seasonal transitions and decoration storage.',
    solution: 'Organized setup/takedown process with proper storage systems.',
    featured: false,
    serviceType: 'addon',
  },
]

// Categories for filtering
export const portfolioCategories = ['All', ...serviceCategories]

// Service type groupings
export const serviceTypeGroups = {
  residential: portfolioItems.filter((item) => item.serviceType === 'residential'),
  commercial: portfolioItems.filter((item) => item.serviceType === 'commercial'),
  addon: portfolioItems.filter((item) => item.serviceType === 'addon'),
}

// Export complete portfolio items
export const completePortfolioItems = portfolioItems
